import React from 'react';
import { DollarSign, CheckCircle, Clock, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatCurrency, formatDate } from './constants';

const CommissionsDetailModal = ({
  isOpen,
  onClose,
  loading,
  detailData,
  onExportExcel,
  onExportPDF,
  onMarkAsPaid
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <DollarSign className="w-5 h-5 text-[#1a5192]" />
            Detalle de Comisiones
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#1a5192]/30 border-t-[#1a5192] rounded-full animate-spin" />
          </div>
        ) : detailData && (
          <div className="space-y-4">
            {/* Doctor info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">{detailData.medico_referente}</h3>
                  <p className="text-sm text-slate-500">
                    Período: {formatDate(detailData.period_start)} al {formatDate(detailData.period_end)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {detailData.pagado ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Pagado
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">
                      <Clock className="w-3 h-3 mr-1" />
                      Pendiente
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Patients table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Paciente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Examen</TableHead>
                    <TableHead>Ojo</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="text-right">Descuento</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Comisión</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailData.patients.map((patient, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{patient.nombre}</TableCell>
                      <TableCell>{patient.fecha}</TableCell>
                      <TableCell>{patient.tipo_examen}</TableCell>
                      <TableCell>{patient.ojo_estudiar || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(patient.precio)}</TableCell>
                      <TableCell className="text-right">
                        {patient.tipo_descuento === 'porcentaje' 
                          ? `${patient.descuento}%` 
                          : formatCurrency(patient.descuento)}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(patient.total)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {formatCurrency(patient.comision)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals */}
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell colSpan={4}>TOTALES</TableCell>
                    <TableCell className="text-right">{formatCurrency(detailData.totals.total_precio)}</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">{formatCurrency(detailData.totals.total_facturado)}</TableCell>
                    <TableCell className="text-right text-emerald-600">
                      {formatCurrency(detailData.totals.total_comision)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={onExportExcel}
                className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button
                variant="outline"
                onClick={onExportPDF}
                className="border-red-400 text-red-500 hover:bg-red-50"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              {!detailData.pagado && (
                <Button
                  onClick={() => onMarkAsPaid({
                    medico_referente: detailData.medico_referente,
                    total_facturado: detailData.totals.total_facturado,
                    comision: detailData.totals.total_comision
                  })}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como Pagado
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommissionsDetailModal;
